import { Component, OnInit, inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseApiService } from '@packages/ng-shared-services';
import { AuthenticationCheckService } from '../../services/auth/authentication-check.service';
import { isFulfillmentAdminUser, postLoginRoute } from '../../services/auth/portal-role';
import { ProgramService } from '../../services/program/program.service';
import { ToastService } from '@packages/ng-shared-services';
import { AUTH_ENDPOINTS } from '@packages/shared-types';

/** Interface for Login response */
interface LoginResponse {
  success: boolean;
  status: number;
  data: {
    id: number;
    jwt_token: string;
    user_name: string;
    user_role: number;
    program_id: number;
    role_name: number;
    role_code?: string;
    portal_type?: string;
    role_level?: number;
    login_type: string;
    token_expires_at: string;
    token_expiry_seconds: number;
  };
}

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  standalone: false,
})
export class SigninComponent implements OnInit {
  // Dependency injection using inject() function
  private readonly apiService = inject(BaseApiService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthenticationCheckService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly programService = inject(ProgramService);

  // Reactive signals for state management
  readonly errorMessage = signal<boolean>(true);
  readonly submitted = signal<boolean>(false);
  readonly inputPasswordType = signal<'password' | 'text'>('password');
  readonly isLoading = signal<boolean>(false);

  // Form groups with proper typing
  loginForm!: FormGroup;

  constructor() {
    this.initializeForms();
    this.checkExistingLogin();
  }

  ngOnInit(): void {
    // SuperAdmin doesn't need to load programs on signin
  }

  // Form control getters for template access
  get c() {
    return this.loginForm.controls;
  }

  /**
   * Initialize login form with validators
   */
  private initializeForms(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required]], // Removed email validator - username can be any format
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  /**
   * Check if user is already logged in and redirect to programs
   */
  private checkExistingLogin(): void {
    if (this.auth.isAuthenticated()) {
      this.router.navigateByUrl(postLoginRoute());
    }
  }

  /**
   * Toggle password visibility with auto-hide after 1 second
   */
  toggleShowPassword(): void {
    const currentType = this.inputPasswordType();
    this.inputPasswordType.set(currentType === 'password' ? 'text' : 'password');

    // Auto-hide password after 1 second for security
    setTimeout(() => {
      this.inputPasswordType.set('password');
    }, 1000);
  }

  /**
   * Handle login form submission
   * Payload format: { LoginForm: { username, password, login_type: 'web' } }
   */
  login(formData: { email: string; password: string }): void {
    this.submitted.set(true);

    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading.set(true);

    // SuperAdmin login payload format
    const loginPayload = {
      LoginForm: {
        program_id: 1,
        email: formData.email,
        password: formData.password,
        login_type: 'web', // Static value
        portal_type: 'super_admin',
      },
    };

    // Custom headers for login API
    const loginHeaders = {
      'X-Program-Id': 'master_admin', // Use 'public' for SuperAdmin login
    };

    this.apiService
      .postWithHeaders(AUTH_ENDPOINTS.login, loginPayload, loginHeaders)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.handleLoginSuccess(res, formData.email);
        },
        error: (err: any) => {
          this.errorMessage.set(false);
          this.isLoading.set(false);
          const errorMsg =
            err.error?.message || err.error?.data?.message || 'Login failed. Please try again.';
          this.toast.error(errorMsg);
        },
      });
  }

  /**
   * Handle successful login response
   * Response format: { success, status, data: { id, jwt_token, user_name, ... } }
   */
  private handleLoginSuccess(res: any, email: string): void {
    const responseBody = res.body;

    if (!responseBody.success || responseBody.status !== 200) {
      this.isLoading.set(false);
      this.toast.error('Login failed. Please try again.');
      return;
    }

    this.errorMessage.set(true);
    const userData = responseBody.data;

    // Store user data in localStorage
    this.toast.success('Login successful');
    localStorage.setItem('user_name', userData.user_name);
    localStorage.setItem('user_id', String(userData.id));
    localStorage.setItem('user_email', email); // Store email for program-login
    localStorage.setItem('program_id', String(userData.program_id));
    localStorage.setItem('user_role', String(userData.user_role));
    localStorage.setItem('role_code', userData.role_code || '');
    localStorage.setItem('portal_type', userData.portal_type || '');
    localStorage.setItem('role_level', String(userData.role_level || 0));
    localStorage.setItem('user_data', JSON.stringify(userData));
    localStorage.setItem('login', 'true');

    // Save authentication token (jwt_token from response)
    this.auth.saveAuth(userData.jwt_token);
    this.submitted.set(false);
    this.isLoading.set(false);

    if (isFulfillmentAdminUser()) {
      this.programService.clearProgram();
    }

    this.router.navigateByUrl(postLoginRoute());
  }
}
